import { ArrowLeft, Check, CircleCheck, Plus, Send, Sparkles } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { toAppError } from '../../api/errors';
import { useAuth } from '../../app/auth';
import { customRole, Routes } from '../../app/routes';
import { useCatalog, useEmployees, useRequestEmployee, useWorkspaces } from '../../data/queries';
import type { Employee, EmployeeModule, Workspace } from '../../domain/models';
import { canRequestEmployee } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import { errorMessage, headerTint, workKindIcon, workKindName } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { EmptyState, ErrorView, Skeleton } from '../core/widgets/common';
import { useToast } from '../core/widgets/toasts';
import { CheckChip, CheckRow, FieldError, QuestionField, TextField } from './fields';
import {
  NEW_WORKSPACE,
  fieldId,
  hireInput,
  initialDraft,
  serverFieldErrors,
  validateHire,
  without,
  worksIn,
  type HireDraft,
  type HirePrefill,
} from './hire';
import { teamHref } from './team';
import './hire.css';

// Asking for one employee from the shelf, or for someone not on it (`/team/hire/custom`): which
// business, the answers to its questions, the connections, what may run without the person from day
// one, and a note. The Talkos team sets the employee up from it (ADR 0010).

export function HireFormPage() {
  const t = useT();
  const { role = '' } = useParams<{ role: string }>();
  const [params] = useSearchParams();
  const { session } = useAuth();
  const custom = role === customRole;
  const catalogQ = useCatalog(!custom);
  const workspacesQ = useWorkspaces();
  const employeesQ = useEmployees();
  const preferred = params.get('workspace');
  // Answers handed over with the link (the demo's tour fills in a sample).
  const prefill = (useLocation().state as { prefill?: HirePrefill } | null)?.prefill;
  // Opened from somewhere down the shelf: the form starts at its top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [role]);

  if (!canRequestEmployee(session)) return <Navigate to={teamHref(preferred ?? undefined)} replace />;

  const module = custom ? undefined : catalogQ.data?.find((m) => m.role === role);
  const failed = [catalogQ, workspacesQ, employeesQ].find((q) => q.isError && !q.data);
  const back = (
    <Link to={Routes.hire(preferred ?? undefined)} className="btn btn-text btn-small hire-back">
      <ArrowLeft size={18} aria-hidden />
      {t('hireTitle')}
    </Link>
  );

  let content: ReactNode;
  if (failed && !(custom && failed === catalogQ)) {
    content = <ErrorView error={failed.error} onRetry={() => void failed.refetch()} />;
  } else if ((!custom && !catalogQ.data) || !workspacesQ.data || !employeesQ.data) {
    content = (
      <div className="hire-detail" aria-busy>
        <div className="hire-aside">
          <Skeleton width={96} height={96} radius={32} />
          <Skeleton width={160} height={26} />
          <Skeleton height={64} />
        </div>
        <Skeleton height={420} radius={22} />
      </div>
    );
  } else if (!custom && !module) {
    content = (
      <EmptyState
        title={t('errorNotFound')}
        action={
          <Link to={Routes.hire(preferred ?? undefined)} className="btn btn-tonal">
            {t('hireTitle')}
          </Link>
        }
      />
    );
  } else {
    content = (
      <HireForm
        key={role}
        module={module}
        workspaces={workspacesQ.data}
        employees={employeesQ.data}
        preferred={preferred}
        prefill={prefill}
      />
    );
  }

  return (
    <div className="page page-wide hire">
      {back}
      {content}
    </div>
  );
}

function HireForm({
  module,
  workspaces,
  employees,
  preferred,
  prefill,
}: {
  module?: EmployeeModule;
  workspaces: Workspace[];
  employees: Employee[];
  preferred: string | null;
  prefill?: HirePrefill;
}) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const request = useRequestEmployee();
  const taken = module ? worksIn(module.role, employees, workspaces) : [];
  const [draft, setDraft] = useState<HireDraft>(() => initialDraft(module, workspaces, taken, preferred, prefill));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const busy = request.isPending;

  const update = (change: Partial<HireDraft>, clears?: string) => {
    setDraft((d) => ({ ...d, ...change }));
    // Changing a field takes its error away; the rest wait for the next try.
    if (clears && errors[clears]) setErrors((all) => without(all, clears));
  };
  const toggle = <T,>(list: T[], value: T, on: boolean) => (on ? [...list, value] : list.filter((x) => x !== value));

  // The fields in the order the form shows them, for focusing the first one to fix.
  const order = [
    fieldId.description,
    fieldId.workspace,
    fieldId.workspaceAbout,
    ...(module?.questions.map((q) => fieldId.answer(q.key)) ?? []),
    fieldId.automatic,
  ];
  const showErrors = (found: Record<string, string>) => {
    setErrors(found);
    const first = order.find((id) => found[id]);
    if (first) document.getElementById(`hire-${first}`)?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const found = validateHire(t, module, draft);
    if (Object.keys(found).length > 0) return showErrors(found);
    try {
      await request.mutateAsync(hireInput(module, draft));
      toast(t('hireSent'));
      const ws = workspaces.find((w) => w.id === draft.workspace);
      void navigate(teamHref(ws?.slug));
      // The request now heads the team page.
      window.scrollTo(0, 0);
    } catch (err) {
      const appError = toAppError(err);
      if (appError.code === 'already_hired') {
        const ws = workspaces.find((w) => w.id === draft.workspace);
        return showErrors({ [fieldId.workspace]: t('hireAlreadyWorks', { workspaces: ws?.name ?? '' }) });
      }
      const { fields, placed } = serverFieldErrors(t, appError, (id) => order.includes(id));
      if (placed) showErrors(fields);
      else toast(errorMessage(t, appError));
    }
  };

  return (
    <div className="hire-detail">
      <aside className="hire-aside" style={module ? ({ '--emp': module.color } as CSSProperties) : undefined}>
        {module ? <ModuleSummary module={module} /> : <CustomSummary />}
      </aside>

      <form className="hire-form" noValidate onSubmit={(e) => void submit(e)} aria-busy={busy}>
        {!module && (
          <FormSection>
            <TextField
              id={`hire-${fieldId.description}`}
              label={t('hireCustomTitle')}
              help={t('hireCustomHelp')}
              value={draft.note}
              onChange={(note) => update({ note }, fieldId.description)}
              error={errors[fieldId.description]}
              maxLength={4000}
              rows={5}
              long
              required
            />
          </FormSection>
        )}

        <FormSection title={t('hireWorkspace')}>
          <WorkspaceChoice
            workspaces={workspaces}
            taken={taken}
            draft={draft}
            error={errors[fieldId.workspace]}
            aboutError={errors[fieldId.workspaceAbout]}
            onChange={(change) => {
              update(change, 'newWorkspaceAbout' in change ? fieldId.workspaceAbout : fieldId.workspace);
            }}
          />
        </FormSection>

        {module && module.questions.length > 0 && (
          <FormSection title={t('hireQuestions')}>
            <div className="hire-fields">
              {module.questions.map((q) => {
                const id = fieldId.answer(q.key);
                return (
                  <QuestionField
                    key={q.key}
                    id={`hire-${id}`}
                    question={q}
                    value={draft.answers[q.key] ?? ''}
                    error={errors[id]}
                    onChange={(v) => update({ answers: { ...draft.answers, [q.key]: v } }, id)}
                  />
                );
              })}
            </div>
          </FormSection>
        )}

        {module && module.connections.length > 0 && (
          <FormSection title={t('hireConnections')}>
            <div className="hire-chips" role="group" aria-label={t('hireConnections')}>
              {module.connections.map((c) => (
                <CheckChip
                  key={c.key}
                  label={c.label}
                  checked={draft.connections.includes(c.key)}
                  onChange={(on) => update({ connections: toggle(draft.connections, c.key, on) })}
                />
              ))}
            </div>
          </FormSection>
        )}

        {module && module.automatable.length > 0 && (
          <FormSection title={t('hireAutomatic')} hint={t('hireAutomaticHint')}>
            <div className="hire-rows" id={`hire-${fieldId.automatic}`} tabIndex={-1} role="group" aria-label={t('hireAutomatic')}>
              {module.automatable.map((k) => (
                <CheckRow
                  key={k}
                  icon={workKindIcon(k)}
                  label={workKindName(t, k)}
                  checked={draft.automatic.includes(k)}
                  onChange={(on) => update({ automatic: toggle(draft.automatic, k, on) }, fieldId.automatic)}
                />
              ))}
            </div>
            <FieldError id={`hire-${fieldId.automatic}`} error={errors[fieldId.automatic]} />
          </FormSection>
        )}

        {/* An employee of one's own is described at the top; a module's form ends with the rest. */}
        {module && (
          <FormSection>
            <TextField id="hire-note" label={t('hireNote')} value={draft.note} onChange={(note) => update({ note })} long />
          </FormSection>
        )}

        <div className="hire-submit">
          <button type="submit" className="btn btn-filled hire-submit-btn" disabled={busy} data-tour="hire-submit">
            {busy ? <span className="iv-spinner" aria-hidden /> : <Send size={18} aria-hidden />}
            {t('hireSubmit')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** A numbered part of the form, as a card. */
function FormSection({ title, hint, children }: { title?: string; hint?: string; children: ReactNode }) {
  return (
    <section className="card hire-section">
      {title && <h2 className="title-medium hire-section-title">{title}</h2>}
      {hint && <p className="body-small muted hire-section-hint">{hint}</p>}
      {children}
    </section>
  );
}

/** The module's face, what it does and where it fits, beside the form (above it on phones). */
function ModuleSummary({ module: m }: { module: EmployeeModule }) {
  const t = useT();
  return (
    <div className="hire-summary" style={{ background: `linear-gradient(to bottom, ${headerTint(m.color)}, var(--surface) 70%)` }}>
      <EmployeeAvatar avatar={m.avatar} color={m.color} size={96} animate label={m.name} />
      <h1 className="headline-medium">{m.name}</h1>
      <p className="label-large hire-card-role">{m.title}</p>
      <p className="body-medium muted">{m.description}</p>
      {m.skills.length > 0 && (
        <div className="hire-summary-block">
          <h2 className="overline">{t('hireSkills')}</h2>
          <ul className="hire-skills">
            {m.skills.map((s) => (
              <li key={s} className="body-medium">
                <Check size={16} strokeWidth={2.6} aria-hidden />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {m.industries.length > 0 && (
        <div className="hire-summary-block">
          <h2 className="overline">{t('hireIndustries')}</h2>
          <ul className="hire-tags">
            {m.industries.map((i) => (
              <li key={i} className="label-medium">
                {i}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CustomSummary() {
  const t = useT();
  return (
    <div className="hire-summary hire-summary-custom">
      <span className="hire-custom-icon" aria-hidden>
        <Sparkles size={34} />
      </span>
      <h1 className="headline-medium">{t('hireCustom')}</h1>
      <p className="body-medium muted">{t('hireCustomBody')}</p>
    </div>
  );
}

/** Which project: one of the organization's (not one where the role already works) or a new one, named
 * and described. */
function WorkspaceChoice({
  workspaces,
  taken,
  draft,
  error,
  aboutError,
  onChange,
}: {
  workspaces: Workspace[];
  taken: Workspace[];
  draft: HireDraft;
  error?: string;
  /** For a new project: its description's. */
  aboutError?: string;
  onChange: (change: Partial<HireDraft>) => void;
}) {
  const t = useT();
  const id = `hire-${fieldId.workspace}`;
  const isNew = draft.workspace === NEW_WORKSPACE;
  const firstFree = workspaces.find((w) => !taken.some((x) => x.id === w.id));
  // The first choice a person can make takes the field's id, so an error can focus it.
  const radioId = (choice: string) => (!isNew && choice === (firstFree?.id ?? NEW_WORKSPACE) ? id : undefined);
  return (
    <div className="hire-field">
      {taken.length > 0 && (
        <p className="body-small hire-taken">
          <CircleCheck size={15} aria-hidden />
          {t('hireAlreadyWorks', { workspaces: taken.map((w) => w.name).join(', ') })}
        </p>
      )}
      <div className="hire-options" role="radiogroup" aria-label={t('hireWorkspace')} aria-describedby={error && !isNew ? `${id}-error` : undefined}>
        {workspaces.map((w) => {
          const isTaken = taken.some((x) => x.id === w.id);
          return (
            <label key={w.id} className={isTaken ? 'hire-option taken' : 'hire-option'}>
              <input
                id={radioId(w.id)}
                type="radio"
                name="hire-workspace"
                value={w.id}
                checked={draft.workspace === w.id}
                disabled={isTaken}
                onChange={() => onChange({ workspace: w.id })}
              />
              {isTaken ? (
                <CircleCheck size={16} className="hire-option-mark" aria-hidden />
              ) : (
                <span className="hire-option-dot" style={{ background: w.color }} aria-hidden />
              )}
              <span className="title-small ellipsis">{w.name}</span>
            </label>
          );
        })}
        <label className="hire-option">
          <input
            id={radioId(NEW_WORKSPACE)}
            type="radio"
            name="hire-workspace"
            value={NEW_WORKSPACE}
            checked={isNew}
            onChange={() => onChange({ workspace: NEW_WORKSPACE })}
          />
          <Plus size={16} className="hire-option-mark" aria-hidden />
          <span className="title-small">{t('hireNewWorkspace')}</span>
        </label>
      </div>
      {isNew ? (
        <div className="hire-fields">
          <TextField
            id={id}
            label={t('hireNewWorkspaceName')}
            value={draft.newWorkspace}
            onChange={(newWorkspace) => onChange({ newWorkspace })}
            error={error}
            maxLength={80}
            required
          />
          {/* The Talkos team sets the employee up from this; a name alone says too little. */}
          <TextField
            id={`hire-${fieldId.workspaceAbout}`}
            label={t('hireNewWorkspaceAbout')}
            help={t('hireNewWorkspaceAboutHelp')}
            value={draft.newWorkspaceAbout}
            onChange={(newWorkspaceAbout) => onChange({ newWorkspaceAbout })}
            error={aboutError}
            maxLength={4000}
            long
            required
          />
        </div>
      ) : (
        <FieldError id={id} error={error} />
      )}
    </div>
  );
}
